import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { Button, Column, Dropdown, Icon, List, Row, Select } from '@pella/sash-framework';
import { isValidArray } from '../../../utilities/validations';

import FilterTag from '../filter-tag';

const ProjectFilters = ({ isPro, onSelectChange, activeFilters, activeSort, onSortChange, productType, caseStudyType }) => {
	const { filter_groups, sort_and_filter } = useStaticQuery(query).contentstackCategoryInspirationConfig;
	const [isMobileOpen, setIsMobileOpen] = useState(false);

	const inspirationFilters = filter_groups.find((group) => (!productType ? group.filter_tag?.[0]?.toLowerCase() === 'all' : group.filter_tag?.[0]?.toLowerCase() === productType?.toLowerCase()))?.group;
	const proFilters = filter_groups.find((group) => group.filter_tag?.[0]?.toLowerCase() === caseStudyType?.toLowerCase())?.group;

	const createSelectColumn = (tags, label) => {
		if (!isValidArray(tags)) return null;

		return (
			<Column
				xs={12}
				className='md:!flex-3/12'
				key={label}
			>
				<Select
					name={label}
					labelText={label}
					onChange={(event) => {
						onSelectChange(event);
						setIsMobileOpen(false);
					}}
					className='cursor-pointer pr-11'
					groupClass='!mb-0'
					aria-label='listbox'
					value={activeFilters[label] ? tags.find((item) => item.filter_tag.includes(activeFilters[label]))?.filter_tag : ''}
				>
					<option value=''>All</option>
					{tags.map((item, index) => (
						<option
							value={item.filter_tag}
							data-url={item}
							key={index}
						>
							{item.display_name}
						</option>
					))}
				</Select>
			</Column>
		);
	};

	const onMobileButtonClick = () => setIsMobileOpen(!isMobileOpen);

	return (
		<>
			<div
				id='project-filters'
				className='inspire-filters relative'
				data-testid='inspire-filters'
			>
				{!isPro && sort_and_filter && <p className='text-overline mb-4 hidden text-neutral-600 md:block'>{sort_and_filter}</p>}

				<Button
					color='secondary'
					className='gap-2 md:!hidden'
					onClick={onMobileButtonClick}
					wide
				>
					<Icon
						name='filter_list'
						size={20}
					/>
					{!isPro ? sort_and_filter : 'Filter'}
				</Button>

				<div className={`border border-t-0 border-neutral-200 bg-neutral-50 p-4 md:border-0 md:bg-transparent md:p-0 ${isMobileOpen ? '' : 'hidden md:block'}`}>
					<Row className='gap-y-4 md:gap-y-0'>
						{!isPro && isValidArray(inspirationFilters) && inspirationFilters.map((item) => createSelectColumn(item.filters, item.category))}
						{isPro && isValidArray(proFilters) && proFilters.map((item) => createSelectColumn(item.filters, item.category))}

						{!isPro && activeSort && (
							<Column
								xs={12}
								className='!static order-first ml-auto md:order-last md:!flex-3/12'
							>
								<Dropdown
									className='z-2 md:absolute md:right-0 md:top-0'
									showCaret
								>
									<Dropdown.Trigger className='flex items-center gap-x-2 uppercase'>
										<Icon
											name='sort'
											weight={300}
											size={16}
										/>
										<span className='text-body-2 font-semibold text-black'>Sort: {activeSort}</span>
									</Dropdown.Trigger>

									<Dropdown.Content
										className='w-48 shadow-lg md:left-auto md:right-0'
										closeOnContentClick
									>
										<p className='text-body-2 mb-2 border-b border-b-neutral-100 pb-2'>
											<button
												type='button'
												onClick={() => onSortChange('Most Recent')}
												className={`${activeSort === 'Most Recent' ? 'font-bold' : ''} hover:text-black focus:text-black`}
											>
												Most Recent
											</button>
										</p>
										<p className='text-body-2 m-0'>
											<button
												type='button'
												onClick={() => onSortChange('Shop The Look')}
												className={`${activeSort === 'Shop The Look' ? 'font-bold' : ''} hover:text-black focus:text-black`}
											>
												Shop The Look
											</button>
										</p>
									</Dropdown.Content>
								</Dropdown>
							</Column>
						)}
					</Row>
				</div>

				{isValidArray(Object.entries(activeFilters)) && (
					<List
						bullets='none'
						className='mt-3 flex gap-3 md:hidden'
					>
						{Object.entries(activeFilters).map((active, index) => {
							if (!active?.[1]) return null;

							let activeTag;

							if (!isPro) {
								activeTag = inspirationFilters?.find((group) => group.category?.toLowerCase() === active?.[0]?.toLowerCase())?.filters?.find((filter) => filter.filter_tag?.includes(active?.[1]));
							} else {
								activeTag = proFilters?.find((group) => group.category?.toLowerCase() === active?.[0]?.toLowerCase())?.filters?.find((filter) => filter.filter_tag?.includes(active?.[1]));
							}

							if (!activeTag) return null;

							return (
								<li key={index}>
									<FilterTag
										name={activeTag?.display_name}
										onClick={() => onSelectChange({ target: { name: active?.[0], value: '' } })}
									/>
								</li>
							);
						})}
					</List>
				)}

				<hr className='my-6 border-neutral-100' />
			</div>
		</>
	);
};

ProjectFilters.propTypes = {
	isPro: PropTypes.bool,
	onProSelectChange: PropTypes.func,
	onSelectChange: PropTypes.func,
	activeFilters: PropTypes.object,
	activeSort: PropTypes.string,
	onSortChange: PropTypes.func,
	productType: PropTypes.string,
	caseStudyType: PropTypes.string,
};

export default ProjectFilters;

export const query = graphql`
	{
		contentstackCategoryInspirationConfig {
			sort_and_filter
			filter_groups {
				filter_tag
				group {
					category
					filters {
						filter_tag
						display_name
					}
				}
			}
		}
	}
`;
